// Helpers of the Database tab (components/DbConsolePanel.vue): statements for a table, CSV, sizes and the
// per-browser history of statements.
import type { DbResult } from '../api/types';

const WRITE = new Set(['INSERT', 'UPDATE', 'DELETE', 'REPLACE']);

/**
 * The statement's first word, upper case, after whitespace, comments and opening parentheses; '' when there is none.
 * Only steers the UI (preview before a write): the API checks every statement itself.
 */
export function firstKeyword(sql: string): string {
  let s = sql;
  for (;;) {
    const before = s;
    s = s.replace(/^\s+/, '').replace(/^\(+/, '');
    if (/^(--(\s|$)|#)/.test(s)) s = s.replace(/^[^\n]*\n?/, '');
    else if (s.startsWith('/*') && !/^\/\*M?!/.test(s)) { const end = s.indexOf('*/'); s = end < 0 ? '' : s.slice(end + 2); }
    if (s === before) break;
  }
  return (/^[A-Za-z_]+/.exec(s)?.[0] ?? '').toUpperCase();
}

export function isWrite(sql: string): boolean {
  return WRITE.has(firstKeyword(sql));
}

/** A quoted identifier. */
export function ident(name: string): string {
  return '`' + name.replace(/`/g, '``') + '`';
}

export function tableQuery(table: string, limit = 100): string {
  return `SELECT * FROM ${ident(table)} LIMIT ${limit}`;
}

export function describeQuery(table: string): string {
  return `DESCRIBE ${ident(table)}`;
}

function csvCell(v: string | null | undefined): string {
  if (v === null || v === undefined) return '';
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** RFC 4180; SQL NULL is an empty field. */
export function toCsv(r: Pick<DbResult, 'columns' | 'rows'>): string {
  const lines = [r.columns.map((c) => csvCell(c.name)).join(',')];
  for (const row of r.rows) lines.push(row.map(csvCell).join(','));
  return lines.join('\r\n') + '\r\n';
}

/** `4345` → `4.3k`, `1048576` bytes → `1.0 MB`. */
export function compact(n: number | null | undefined, unit: 'rows' | 'bytes'): string {
  if (n === null || n === undefined) return '';
  if (unit === 'bytes') {
    const u = ['B', 'KB', 'MB', 'GB', 'TB'];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${i === 0 ? v : v.toFixed(1)} ${u[i]}`;
  }
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

const HISTORY = 20;
const key = (env: string) => `peak-ui.db-history.${env}`;

export interface HistoryEntry { database: string | null; sql: string }

/** The last statements run in this browser for the environment, newest first; empty when storage is unavailable. */
export function loadHistory(env: string): HistoryEntry[] {
  try {
    const raw = window.localStorage.getItem(key(env));
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list) ? list.filter((e): e is HistoryEntry => !!e && typeof (e as HistoryEntry).sql === 'string').slice(0, HISTORY) : [];
  } catch {
    return [];
  }
}

export function remember(env: string, entry: HistoryEntry): HistoryEntry[] {
  const next = [entry, ...loadHistory(env).filter((e) => e.sql !== entry.sql || e.database !== entry.database)].slice(0, HISTORY);
  try {
    window.localStorage.setItem(key(env), JSON.stringify(next));
  } catch {
    // storage blocked or full: the history just is not kept
  }
  return next;
}
